import { useSyncExternalStore } from 'react';

// The teacher side's language, English or Japanese, one at a time: kept on this laptop apart from the screen language
// (lang.ts), so a teacher can work in English while the projector shows the class Japanese, or the other way round.
// It starts in the laptop's own language; Settings and the sign-in page switch it. Strings are written like the
// student side's, "English · 日本語", or as two words built at run time with hL(en, ja).

export type HostLang = 'en' | 'ja';
const KEY = 'gk-host-lang';
const heard = new Set<() => void>();

function saved(): HostLang | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'en' || v === 'ja' ? v : null;
  } catch {
    return null;
  }
}
const device = (): HostLang => (typeof navigator !== 'undefined' && /^en/i.test(navigator.language || '') ? 'en' : 'ja');
let cur: HostLang = saved() ?? device();

export const hostLang = () => cur;
export const hostLangSaved = () => saved() !== null;
export function setHostLang(l: HostLang) {
  try {
    localStorage.setItem(KEY, l);
  } catch {
    /* private window: it holds until the page closes */
  }
  if (l === cur) return;
  cur = l;
  heard.forEach((f) => f());
}
const listen = (f: () => void) => {
  heard.add(f);
  return () => void heard.delete(f);
};
// the teacher side's root calls this, so every screen under it redraws when the language changes
export const useHostLang = () => useSyncExternalStore(listen, hostLang, hostLang);

// "English · 日本語": the half for the teacher side's language (a string with no Japanese half is the same in both).
// The split is the first " · " with Japanese within a few characters after it, so an English half can hold its own
// " · " and a Japanese half can start with a mark or a word like Google ("● ", "▶ ", "Googleで")
const SPLIT = / · (?=[^·]{0,12}?[　-ヿ一-鿿！-～])/;
export function ht(text: string): string {
  const at = text.search(SPLIT);
  if (at < 0) return text;
  return cur === 'en' ? text.slice(0, at) : text.slice(at + 3);
}
export const hL = <T,>(en: T, ja: T): T => (cur === 'en' ? en : ja);
